import { useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from "react-query";

import logger from '../utility/logger.mjs';
import { timeRanges } from '../utility/influxChartConfig.mjs';
import { 
  useKeyValueQuery,
  usePendingSettingsQuery,
  usePhonenumbersQuery,
  useUiDataQuery,
  useUiDescQuery,
} from '../queries/deviceQuery.js';
import { 
  deviceAdded, 
  selectCurrentDevice, 
  selectCurrentDataIsLoaded,
  selectCurrentActivationTime,
} from '../data/devicesSlice.js';
import {
  currentPageSet,
  selectCurrentPage,
  selectLoginType,
  timeRangeSet
} from '../data/uiSlice.js';
import SubPageDevices from './SubPageDevices.js';
import SubPageLocations from './SubPageLocations.js';
import SetupDialog from "../components/SetupDialog.js";
import TermsDialog from '../components/TermsDialog.js';

import {
  Box,
  CircularProgress,
  Tab,
  Tabs,
} from '@mui/material/';

const subPage = {
  DEVICES: SubPageDevices,
  LOCATIONS: SubPageLocations,
};

const MainPage = ({t}) => {
  const dispatch = useDispatch();
  const loginType = useSelector(selectLoginType);
  const currentSubPage = useSelector(selectCurrentPage);
  const currentMainPage = ['DEVICE-LIST', 'MAIN', 'DATA', 'DEVICES'].includes(currentSubPage)
    ? 'DEVICES'
    : 'LOCATIONS'
  const PUBLIC_VERSION = !!Number(process.env.REACT_APP_IS_PUBLIC_VERSION);
  const dataIsLoaded = useSelector(selectCurrentDataIsLoaded);
  const device = useSelector(selectCurrentDevice);
  const deviceId = device.id;

  useEffect(() => {
    if(["telegram","pin"].includes(loginType)) {
      dispatch(currentPageSet("DATA"));
    }
    else{
      dispatch(currentPageSet("LOCATIONS"));
    }
  }, [])

  const { data:phonenumbers, status:status2 } = usePhonenumbersQuery({
    deviceId,
    enabled: !dataIsLoaded,
  });

  const { data:uiDesc, status:status3 } = useUiDescQuery({
    deviceId,
    enabled: !dataIsLoaded,
  });

  console.log("uiDesc",uiDesc)
  
  const { data:uiData, status:status4 } = useUiDataQuery({
    deviceId, 
    // construct an array of unique jsonkeys and dbTargets
    jsonkeys: [... new Set(uiDesc?.filter(n => n?.id != null).map((row) => row.jsonkeys)?.flat(1))].filter(n => n),
    dbTargets: [... new Set(uiDesc?.filter(n => n?.id != null).map((row) => row.dbTarget)?.flat(1))].filter(n => n),
    commTech: device?.commTech ?? "LTE-M",
    enabled: !!uiDesc 
  });

  const { data:pendingSettings, status:status5, isSuccess:isSuccess5 } = usePendingSettingsQuery({
    deviceId,
    settingsId: Math.max(0, uiData?.map( row => row.settingsId)),
    enabled: !!uiData
  })

  const { data:keyValues, status:status6, isSuccess:isSuccess6, isError:keyValuesIsError } = useKeyValueQuery({
    device, 
    uiDesc, 
    enabled: !!uiDesc 
  });
  
  // parse json strings and construct the data object 
  const data = {
    ...device,
    phonenumbers,
    settings: uiData?.reduce((acc, cur) => (cur?.value == null || cur?.jsonkey == null ? acc : {...acc, [cur.jsonkey]: cur.value}), {}) ?? null,
    dbSettings: uiData?.reduce((acc, cur) => (cur?.value == null || cur?.dbTarget == null ? acc : {...acc, [cur.dbTarget]: cur.value}), {}) ?? null,
    msgId: uiData?.reduce((acc, cur) => (cur?.msgId == null ? acc : {...acc, [cur.jsonkey]: cur.msgId}), {}) ?? null,
    //pendingSettings: (pendingSettings == null || pendingSettings?.length === 0)  ? null : pendingSettings, // set null if array is []
    settingsId: uiData?.reduce((acc, cur) => ((acc > cur.settingsId) ? acc : cur.settingsId), 0), // max settingsId
    settingsTime: uiData?.[0]?.timestamp,
    uiGeneralDesc: uiDesc?.filter(row => row?.id == null)?.[0],
    uiDesc: uiDesc
      ?.filter(row => row?.id != null) // filter out general ui settings
      ?.filter(row => !!Number(row.dev) ? !PUBLIC_VERSION : true) // filter dev version elements if public version
      .map( (row) => {
        const {influxKeySuffix, maxRealVal, minRealVal, step} = uiData?.find( r => (r.jsonkey === row?.jsonkeys?.flat(1)?.[0])) ?? {};
        return ({
            ...row, 
            influxKey: row?.influxKey + (influxKeySuffix ?? ""),
            minMaxRealValues: 
              row?.jsonkeys?.reduce( (acc, cur, i) => ({
                ...acc, 
                [cur]: {
                  max: uiData?.find( r => (r.jsonkey === cur))?.maxRealVal, 
                  min: uiData?.find( r => (r.jsonkey === cur))?.minRealVal, 
                } 
              }), {}) ?? {}, 
            step,
        })
      }),
    data: keyValues != null ? {...keyValues} : {},
  };

  if(isSuccess5 && (status6 === "success" || status6 === "error") && !dataIsLoaded){
    dispatch(deviceAdded(data));
    dispatch(timeRangeSet(timeRanges(data?.data?.referenceValues?.time)[data?.uiGeneralDesc?.defaultTimeRange ?? 4]));
  }

  const handleSubPageChange = (_, newValue) => {
    dispatch(currentPageSet(newValue));
  };

  function SubPageSelector() {
    const SelectedSubPage = subPage[currentMainPage] ?? SubPageLocations
    return <SelectedSubPage />;
  };

  const SetupDialogs = () => {
    if(dataIsLoaded){
      return (
        <>
          <SetupDialog />
          <TermsDialog /> 
        </>
      )
    }
    return false;
  }

  const SubPageTabs = () => {
    if(loginType !== "pin"){
      return (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={currentMainPage}
            onChange={handleSubPageChange}
            scrollButtons="auto"
            allowScrollButtonsMobile
            centered
            >
              <Tab value="LOCATIONS" label={t('locations-tab')} />
              <Tab value="DEVICES" label={t('devices-tab')} />
          </Tabs>
        </Box>
      )
    }
  }

  const Content = () => {
    if((status6 === "success" || status6 === "error") && isSuccess5 ){
      return <SubPageSelector />
    }
    return <CircularProgress />
  }

  return (
    <>
      <SetupDialogs />
      <SubPageTabs />
      <Box sx={{my:3, mx:1}}>
        <Content />
      </Box>
    </>
  )
}

export default withTranslation()(MainPage);